import { useState } from "react";
import { APP_NAME } from "@grundbesitz/shared";
import type { RoomView } from "@grundbesitz/shared";
import { useStore } from "../../lib/store";
import { SoundToggle } from "../SoundToggle";
import { ThemeToggle } from "../ThemeToggle";
import { VoiceToggle } from "../VoiceToggle";
import { copyText } from "../lobby/clipboard";
import { Button } from "../ui/Button";
import { ConfirmDialog } from "./ConfirmDialog";
import { useAct, useCountdown } from "./hooks";
import { ClockIcon, FlagIcon, KeyboardIcon, LinkIcon } from "./icons";
import { useUiStore } from "./uiStore";

function formatClock(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

/** Countdown chip. Turns red in the last ten seconds. */
function TimerChip({ label, seconds, urgentBelow }: { label: string; seconds: number; urgentBelow?: number }) {
  const urgent = urgentBelow !== undefined && seconds <= urgentBelow;
  return (
    <span
      role="timer"
      aria-label={`${label}: noch ${formatClock(seconds)}`}
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-sm font-semibold tabular-nums ${
        urgent ? "border-danger text-danger" : "border-line text-ink-2"
      }`}
    >
      <ClockIcon size={16} />
      <span className="sr-only">{label}</span>
      {formatClock(seconds)}
    </span>
  );
}

export function GameHeader({ room, canResign }: { room: RoomView; canResign: boolean }) {
  const toast = useStore((s) => s.toast);
  const turnSeconds = useCountdown(room.options.turnTimerSeconds > 0 ? room.timers.turnEndsAt : null);
  const gameSeconds = useCountdown(room.timers.gameEndsAt);
  const [confirming, setConfirming] = useState(false);
  const { run } = useAct();
  const setHotkeyHelp = useUiStore((s) => s.setHotkeyHelp);

  const copyLink = async () => {
    const ok = await copyText(`${window.location.origin}/${room.code}`);
    toast(ok ? "Einladungslink kopiert." : "Kopieren nicht möglich. Der Raum-Code lautet " + room.code + ".", ok ? "success" : "error");
  };

  return (
    <header className="flex-none border-b border-line bg-surface pt-[env(safe-area-inset-top)]">
      <div className="flex items-center gap-2 px-3 py-1 sm:px-4">
        <p className="hidden font-display text-xl font-bold leading-none tracking-[-0.03em] sm:block">{APP_NAME}</p>
        <button
          type="button"
          onClick={copyLink}
          title="Einladungslink kopieren"
          className="group inline-flex min-h-11 items-center gap-2 rounded-lg px-2 hover:bg-surface-2 sm:ml-2"
        >
          <span className="sr-only">Raum-Code {room.code}. Einladungslink kopieren</span>
          <span aria-hidden="true" className="font-mono text-base font-bold tracking-[0.14em]">
            {room.code}
          </span>
          <span aria-hidden="true" className="text-ink-2 group-hover:text-ink">
            <LinkIcon size={16} />
          </span>
        </button>

        <div className="ml-auto flex items-center gap-1.5">
          {turnSeconds !== null && <TimerChip label="Zugzeit" seconds={turnSeconds} urgentBelow={10} />}
          {gameSeconds !== null && <TimerChip label="Restzeit des Spiels" seconds={gameSeconds} urgentBelow={60} />}
          <button
            type="button"
            onClick={() => setHotkeyHelp(true)}
            title="Tastenkürzel (?)"
            aria-label="Tastenkürzel anzeigen"
            aria-keyshortcuts="?"
            className="hidden size-11 place-items-center rounded-lg text-ink-2 hover:bg-surface-2 hover:text-ink pointer-fine:grid"
          >
            <KeyboardIcon size={20} />
          </button>
          <SoundToggle />
          <VoiceToggle />
          <ThemeToggle />
          {canResign && (
            <Button variant="ghost" className="px-2.5 text-danger" onClick={() => setConfirming(true)}>
              <FlagIcon size={18} />
              <span className="hidden sm:inline">Aufgeben</span>
              <span className="sr-only sm:hidden">Aufgeben</span>
            </Button>
          )}
        </div>
      </div>
      <ConfirmDialog
        open={confirming}
        title="Wirklich aufgeben?"
        confirmLabel="Ja, aufgeben"
        onCancel={() => setConfirming(false)}
        onConfirm={() => {
          setConfirming(false);
          void run({ type: "RESIGN" });
        }}
      >
        Du scheidest aus dem Spiel aus. Deine Gebäude gehen zurück an die Bank, deine Grundstücke werden einzeln versteigert. Danach schaust du zu.
      </ConfirmDialog>
    </header>
  );
}
